import React from 'react'
import { theme } from '../../../core/design-system/theme'
import { useMyBookingsViewModel } from '../hooks/useMyBookingsViewModel'

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function durationLabel(start, end) {
  if (!start || !end) return ''
  const hours = Math.round((new Date(end) - new Date(start)) / 3600000)
  return `${hours}h`
}

const cardStyle = {
  background: theme.colors.card,
  border: '1px solid ' + theme.colors.border,
  borderRadius: 14,
  padding: 14,
  boxShadow: '0 2px 8px rgba(92,61,46,0.05)',
  marginBottom: 12,
}

function StatusPill({ status }) {
  const confirmed = status === 'confirmed'
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 9px',
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: theme.fonts.body,
        background: confirmed ? theme.colors.successBg : theme.colors.border,
        color: confirmed ? theme.colors.successText : theme.colors.textSecondary,
        textTransform: 'capitalize',
        flexShrink: 0,
      }}
    >
      {status}
    </span>
  )
}

function BookingCard({ booking }) {
  const [hovered, setHovered] = React.useState(false)
  return (
    <div
      style={{
        ...cardStyle,
        opacity: hovered ? 0.85 : 1,
        transition: 'opacity 0.15s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontFamily: theme.fonts.body,
              fontWeight: 700,
              fontSize: 14,
              color: theme.colors.textPrimary,
            }}
          >
            {formatDate(booking.scheduledStart)}
          </p>
          <p
            style={{
              margin: '4px 0 0',
              fontFamily: theme.fonts.body,
              fontSize: 13,
              color: theme.colors.textSecondary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {booking.locationLabel}
          </p>
        </div>
        <StatusPill status={booking.status} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
        <span style={{ fontFamily: theme.fonts.body, fontSize: 13, color: theme.colors.textTertiary }}>
          {durationLabel(booking.scheduledStart, booking.scheduledEnd)}
        </span>
        <span
          style={{
            fontFamily: theme.fonts.body,
            fontWeight: 700,
            fontSize: 15,
            color: theme.colors.terracotta,
          }}
        >
          ${Number(booking.totalAmount).toFixed(2)}
        </span>
      </div>
    </div>
  )
}

function CalendarIcon() {
  return (
    <svg
      width="52"
      height="52"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      stroke={theme.colors.terracottaLight}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

export default function MyBookingsView({ repository, session }) {
  const { status, bookings, error } = useMyBookingsViewModel({ repository, session })

  return (
    <main
      className="page-enter"
      style={{
        background: theme.colors.cream,
        minHeight: '100vh',
        paddingBottom: 90,
        paddingTop: 24,
      }}
    >
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '0 20px' }}>
        <h1
          style={{
            fontFamily: theme.fonts.display,
            fontWeight: 600,
            fontSize: 22,
            color: theme.colors.textPrimary,
            margin: '0 0 20px',
          }}
        >
          My Bookings
        </h1>

        {status === 'loading' && (
          <div aria-label="Loading bookings" aria-busy="true">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  height: 84,
                  background: theme.colors.bgSecondary,
                  borderRadius: 14,
                  marginBottom: 12,
                  opacity: 0.7,
                }}
              />
            ))}
          </div>
        )}

        {status === 'failed' && (
          <p
            role="alert"
            style={{
              fontFamily: theme.fonts.body,
              color: theme.colors.errorText,
              padding: 16,
              background: theme.colors.errorBg,
              borderRadius: 12,
              margin: 0,
            }}
          >
            {error}
          </p>
        )}

        {status === 'loaded' && bookings.length === 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: 60,
              gap: 12,
              textAlign: 'center',
            }}
          >
            <CalendarIcon />
            <h2
              style={{
                fontFamily: theme.fonts.display,
                fontWeight: 600,
                fontSize: 18,
                color: theme.colors.textPrimary,
                margin: 0,
              }}
            >
              No bookings yet
            </h2>
            <p
              style={{
                fontFamily: theme.fonts.body,
                fontSize: 14,
                color: theme.colors.textSecondary,
                margin: 0,
                maxWidth: 240,
              }}
            >
              When you book a Nonna, your bookings will appear here.
            </p>
          </div>
        )}

        {status === 'loaded' && bookings.length > 0 && (
          <div>
            {bookings.map((booking) => (
              <BookingCard key={booking.id} booking={booking} />
            ))}
          </div>
        )}
      </div>
    </main>
  )
}
